/**
 * Sync-Layer fuer Trainingsfortschritt.
 *
 * Architektur:
 *   - localStorage ist die unmittelbare Persistenz (siehe setProgress.ts)
 *   - Dieses Repository hebt Aenderungen optional in PocketBase
 *   - Bei Login: Pull aller Server-Eintraege + Merge mit lokalen (server gewinnt bei updated_at-Konflikt)
 *   - Bei Schreibvorgang: Eintrag in Queue, Flush wenn online + auth
 *
 * Im Anonym-Modus (kein PocketBase oder kein Login) sind alle Operationen no-ops.
 *
 * Erwartetes PocketBase-Schema (Collection `progress`):
 *   - user (relation → users, required)
 *   - date (text, required)         — YYYY-MM-DD
 *   - exercise_id (text, required)
 *   - sets (json, required)         — int[]
 *   - updated_at (date, autodate update + create)
 *   - Index: unique (user, date, exercise_id)
 *   - Rules: alle CRUD nur wenn `user = @request.auth.id`
 */

import type PocketBase from 'pocketbase';
import { getPocketBase, isCloudConfigured } from './pocketbaseClient';
import { getSession, getUserId, subscribe as subscribeAuth } from './auth';

const QUEUE_KEY = 'sync:queue:v1';
const LAST_PULL_KEY = 'sync:lastPull:v1';
const COLLECTION = 'progress';

export interface ProgressEntry {
  dayKey: string; // YYYY-MM-DD
  exerciseId: string;
  sets: readonly number[];
  updatedAt: number; // ms epoch
}

interface QueuedUpload {
  dayKey: string;
  exerciseId: string;
  sets: number[];
  updatedAt: number; // ms epoch
}

interface PbProgressRecord {
  id: string;
  user: string;
  date: string;
  exercise_id: string;
  sets: number[];
  updated_at: string;
}

type ApplyRemoteFn = (entries: readonly ProgressEntry[]) => void;

interface RepoState {
  queue: QueuedUpload[];
  flushing: boolean;
  flushScheduled: boolean;
  online: boolean;
  applyRemote: ApplyRemoteFn | null;
  authUnsub: (() => void) | null;
  syncedAfterLogin: string | null;
}

const state: RepoState = {
  queue: [],
  flushing: false,
  flushScheduled: false,
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  applyRemote: null,
  authUnsub: null,
  syncedAfterLogin: null,
};

let initialized = false;

function loadQueue(): QueuedUpload[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isQueuedUpload);
  } catch {
    return [];
  }
}

function isQueuedUpload(value: unknown): value is QueuedUpload {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['dayKey'] === 'string' &&
    typeof v['exerciseId'] === 'string' &&
    Array.isArray(v['sets']) &&
    (v['sets'] as unknown[]).every((n) => typeof n === 'number') &&
    typeof v['updatedAt'] === 'number'
  );
}

function persistQueue(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (state.queue.length === 0) {
      localStorage.removeItem(QUEUE_KEY);
    } else {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(state.queue));
    }
  } catch {
    // Quota voll — Queue lebt trotzdem in-memory weiter
  }
}

export function initRepository(applyRemote: ApplyRemoteFn): void {
  if (initialized) {
    state.applyRemote = applyRemote;
    return;
  }
  initialized = true;
  state.applyRemote = applyRemote;
  state.queue = loadQueue();

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      state.online = true;
      scheduleFlush();
    });
    window.addEventListener('offline', () => {
      state.online = false;
    });
  }

  if (isCloudConfigured()) {
    state.authUnsub = subscribeAuth(() => {
      void onAuthChange();
    });
    void onAuthChange();
  }
}

async function onAuthChange(): Promise<void> {
  const userId = getUserId();
  if (!userId) {
    state.syncedAfterLogin = null;
    return;
  }
  if (state.syncedAfterLogin === userId) {
    scheduleFlush();
    return;
  }
  state.syncedAfterLogin = userId;
  await syncOnLogin();
}

async function syncOnLogin(): Promise<void> {
  const pb = getPocketBase();
  const userId = getUserId();
  if (!pb || !userId) return;

  try {
    const remote = await pullRemoteSnapshot(pb, userId);
    const local = readLocalEntries();
    const { mergedRemote, toUpload } = mergeSnapshots(local, remote);

    state.applyRemote?.(mergedRemote);

    for (const entry of toUpload) {
      enqueueInternal({
        dayKey: entry.dayKey,
        exerciseId: entry.exerciseId,
        sets: [...entry.sets],
        updatedAt: entry.updatedAt,
      });
    }
    persistQueue();

    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(LAST_PULL_KEY, String(Date.now()));
      } catch {
        // ignore
      }
    }

    await flush();
  } catch (err) {
    console.warn('[pocketbase] sync on login failed:', err);
  }
}

async function pullRemoteSnapshot(
  pb: PocketBase,
  userId: string,
): Promise<readonly ProgressEntry[]> {
  const records = await pb.collection(COLLECTION).getFullList<PbProgressRecord>({
    filter: `user = "${userId}"`,
    fields: 'id,user,date,exercise_id,sets,updated_at',
  });
  return records.map((row) => ({
    dayKey: row.date,
    exerciseId: row.exercise_id,
    sets: Object.freeze(Array.isArray(row.sets) ? row.sets.slice() : []),
    updatedAt: Date.parse(row.updated_at),
  }));
}

function readLocalEntries(): readonly ProgressEntry[] {
  if (typeof localStorage === 'undefined') return [];
  const PREFIX = 'progress:v1:';
  const out: ProgressEntry[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    const rest = key.slice(PREFIX.length);
    const sep = rest.indexOf(':');
    if (sep === -1) continue;
    const dayKey = rest.slice(0, sep);
    const exerciseId = rest.slice(sep + 1);
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((n) => typeof n === 'number')) {
        out.push({
          dayKey,
          exerciseId,
          sets: Object.freeze(parsed.slice()),
          // Lokale Eintraege haben keinen Timestamp → wir nehmen "Mitternacht des Tages"
          // als pessimistischen Wert, damit Server-Eintraege mit echtem Timestamp
          // im Konfliktfall gewinnen.
          updatedAt: Date.parse(`${dayKey}T00:00:00Z`),
        });
      }
    } catch {
      // ignore corrupt entry
    }
  }
  return out;
}

interface MergeResult {
  mergedRemote: readonly ProgressEntry[];
  toUpload: readonly ProgressEntry[];
}

export function mergeSnapshots(
  local: readonly ProgressEntry[],
  remote: readonly ProgressEntry[],
): MergeResult {
  const byKey = new Map<string, ProgressEntry>();
  for (const e of remote) {
    byKey.set(`${e.dayKey}:${e.exerciseId}`, e);
  }
  const toUpload: ProgressEntry[] = [];
  for (const e of local) {
    const k = `${e.dayKey}:${e.exerciseId}`;
    const server = byKey.get(k);
    if (!server) {
      byKey.set(k, e);
      toUpload.push(e);
    } else if (e.updatedAt > server.updatedAt) {
      byKey.set(k, e);
      toUpload.push(e);
    }
  }
  return {
    mergedRemote: Array.from(byKey.values()),
    toUpload,
  };
}

function enqueueInternal(item: QueuedUpload): void {
  const k = `${item.dayKey}:${item.exerciseId}`;
  state.queue = state.queue.filter((q) => `${q.dayKey}:${q.exerciseId}` !== k);
  state.queue.push(item);
}

export function recordChange(
  dayKey: string,
  exerciseId: string,
  sets: readonly number[],
): void {
  if (!initialized) return;
  enqueueInternal({
    dayKey,
    exerciseId,
    sets: [...sets],
    updatedAt: Date.now(),
  });
  persistQueue();
  scheduleFlush();
}

function scheduleFlush(): void {
  if (state.flushScheduled || state.flushing) return;
  if (!state.online || !getSession()) return;
  state.flushScheduled = true;
  // Debounce 250 ms — buendelt schnelle Toggle-Folgen zu einem Roundtrip.
  setTimeout(() => {
    state.flushScheduled = false;
    void flush();
  }, 250);
}

async function flush(): Promise<void> {
  if (state.flushing) return;
  const pb = getPocketBase();
  const userId = getUserId();
  if (!pb || !userId || !state.online) return;
  if (state.queue.length === 0) return;

  state.flushing = true;
  const batch = state.queue.slice();
  try {
    await Promise.all(
      batch.map(async (item) => {
        if (item.sets.length === 0) {
          await deleteProgress(pb, userId, item.dayKey, item.exerciseId);
        } else {
          await upsertProgress(pb, userId, item);
        }
      }),
    );

    const processedKeys = new Set(
      batch.map((b) => `${b.dayKey}:${b.exerciseId}:${b.updatedAt}`),
    );
    state.queue = state.queue.filter(
      (q) => !processedKeys.has(`${q.dayKey}:${q.exerciseId}:${q.updatedAt}`),
    );
    persistQueue();
  } catch (err) {
    console.warn('[pocketbase] flush failed:', err);
    // Queue bleibt erhalten → naechster Versuch beim naechsten Trigger
  } finally {
    state.flushing = false;
  }
}

async function findExisting(
  pb: PocketBase,
  userId: string,
  dayKey: string,
  exerciseId: string,
): Promise<PbProgressRecord | null> {
  try {
    const filter = `user = "${userId}" && date = "${dayKey}" && exercise_id = "${exerciseId}"`;
    return await pb.collection(COLLECTION).getFirstListItem<PbProgressRecord>(filter);
  } catch (err) {
    if (isNotFoundError(err)) return null;
    throw err;
  }
}

async function upsertProgress(
  pb: PocketBase,
  userId: string,
  item: QueuedUpload,
): Promise<void> {
  const existing = await findExisting(pb, userId, item.dayKey, item.exerciseId);
  const payload = {
    user: userId,
    date: item.dayKey,
    exercise_id: item.exerciseId,
    sets: item.sets,
  };
  if (existing) {
    await pb.collection(COLLECTION).update(existing.id, payload);
  } else {
    await pb.collection(COLLECTION).create(payload);
  }
}

async function deleteProgress(
  pb: PocketBase,
  userId: string,
  dayKey: string,
  exerciseId: string,
): Promise<void> {
  const existing = await findExisting(pb, userId, dayKey, exerciseId);
  if (!existing) return;
  await pb.collection(COLLECTION).delete(existing.id);
}

function isNotFoundError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const status = (err as { status?: unknown }).status;
  return status === 404;
}

export function getPendingCount(): number {
  return state.queue.length;
}

export function isOnline(): boolean {
  return state.online;
}

/** Test-Helper. NICHT im App-Code verwenden. */
export function __resetRepositoryForTests(): void {
  state.queue = [];
  state.flushing = false;
  state.flushScheduled = false;
  state.online = true;
  state.applyRemote = null;
  state.authUnsub?.();
  state.authUnsub = null;
  state.syncedAfterLogin = null;
  initialized = false;
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(QUEUE_KEY);
    localStorage.removeItem(LAST_PULL_KEY);
  }
}
